import type { IMappedGeoMappingItem } from 'partner360/components/filters'

import type { ICockpitPartnerData } from '../../model/types'
import {
  UNMAPPED_GEO_KEY,
  UNMAPPED_GEO_LABEL,
  UNMAPPED_GEO_ORDER,
} from '../revenueAndSobHierarchy/constants'
import {
  buildGeoMappingTree,
  type IGeoMappingTree,
} from '../revenueAndSobHierarchy/geoMappingHierarchy'
import { normalizeGeoKey } from '../revenueAndSobHierarchy/normalizeGeoKey'

import {
  getCockpitInitiativeGroups,
  sumCockpitInitiativeAmounts,
} from './getCockpitInitiativeGroups'
import type {
  ICockpitInitiativeHierarchyRow,
  ICockpitInitiativeRow,
  TCockpitInitiativeGroupField,
  TCockpitInitiativesHierarchyOrder,
} from './types'

export const COCKPIT_INITIATIVE_TOTAL_ROW_ID = '__initiative-total__'

export type ICockpitInitiativeRowWithGeo = ICockpitInitiativeRow & {
  countryCode: string
}

export const enrichCockpitInitiativeRowsWithGeo = (
  rows: ICockpitInitiativeRow[],
  partners: ICockpitPartnerData[]
): ICockpitInitiativeRowWithGeo[] => {
  const partnerById = new Map(partners.map((partner) => [partner.PartnerId, partner]))
  return rows.map((row) => {
    const partner = partnerById.get(row.partnerId)

    return {
      ...row,
      countryCode: partner?.CountryCode?.trim() ?? '',
    }
  })
}

interface IGeoBucket {
  key: string
  label: string
  position: number
  rows: ICockpitInitiativeRowWithGeo[]
}

const getGeoPlacement = (
  row: ICockpitInitiativeRowWithGeo,
  geoDepth: number,
  geoTree: IGeoMappingTree
): IMappedGeoMappingItem | 'unmapped' | undefined => {
  const chain = geoTree.chainByCountry.get(normalizeGeoKey(row.countryCode))

  if (!chain?.length) {
    return geoDepth === 0 ? 'unmapped' : undefined
  }

  return chain[geoDepth]
}

const getGeoBuckets = (
  rows: ICockpitInitiativeRowWithGeo[],
  geoDepth: number,
  geoTree: IGeoMappingTree
): IGeoBucket[] => {
  const buckets = new Map<string, IGeoBucket>()

  rows.forEach((row) => {
    const placement = getGeoPlacement(row, geoDepth, geoTree)

    if (placement === undefined) return

    const isUnmapped = placement === 'unmapped'
    const key = isUnmapped ? UNMAPPED_GEO_KEY : placement.itemId
    const bucket = buckets.get(key)

    if (bucket) {
      bucket.rows.push(row)
    } else {
      buckets.set(key, {
        key,
        label: isUnmapped ? UNMAPPED_GEO_LABEL : placement.name,
        position: isUnmapped ? UNMAPPED_GEO_ORDER : (geoTree.positionById.get(key) ?? 0),
        rows: [row],
      })
    }
  })

  return [...buckets.values()].sort((left, right) => left.position - right.position)
}

const getPartnerBuckets = (rows: ICockpitInitiativeRowWithGeo[]) => {
  const buckets = new Map<string, ICockpitInitiativeRowWithGeo[]>()

  rows.forEach((row) => {
    const bucket = buckets.get(row.partnerId)

    if (bucket) {
      bucket.push(row)
    } else {
      buckets.set(row.partnerId, [row])
    }
  })

  return [...buckets.entries()]
}

const getAmounts = (rows: ICockpitInitiativeRow[]) => ({
  goal: sumCockpitInitiativeAmounts(rows.map(({ goal }) => goal)),
  incremental: sumCockpitInitiativeAmounts(rows.map(({ incremental }) => incremental)),
  mdfPlanned: sumCockpitInitiativeAmounts(rows.map(({ mdfPlanned }) => mdfPlanned)),
  count: rows.length,
})

const getInitiativeEntryIds = (rows: ICockpitInitiativeRow[]) =>
  Array.from(new Set(rows.map(({ entryId }) => entryId)))

interface IProductRowsOptions {
  parentRowId?: string | null
  rowIdPrefix?: string
  geoKey?: string
  geoLabel?: string
}

interface ICockpitProductGroup {
  name: string
  rows: ICockpitInitiativeRowWithGeo[]
  buStrategicInitiativeTips: string[]
}

const getOfferingGroups = (rows: ICockpitInitiativeRowWithGeo[]): ICockpitProductGroup[] => {
  const rowsByOffering = new Map<string, ICockpitInitiativeRowWithGeo[]>()

  rows.forEach((row) => {
    rowsByOffering.set(row.offering, [...(rowsByOffering.get(row.offering) ?? []), row])
  })

  return Array.from(rowsByOffering, ([name, groupRows]) => ({
    name,
    rows: groupRows,
    buStrategicInitiativeTips: [],
  })).sort(
    (left, right) =>
      (getAmounts(right.rows).goal ?? 0) - (getAmounts(left.rows).goal ?? 0) ||
      left.name.localeCompare(right.name)
  )
}

const getBuStrategicInitiativeChildGroups = (
  rows: ICockpitInitiativeRowWithGeo[],
  rootName: string,
  groupField: TCockpitInitiativeGroupField
): ICockpitProductGroup[] => {
  if (groupField !== 'development') {
    return []
  }

  const rowsByLeaf = new Map<string, ICockpitInitiativeRowWithGeo[]>()

  rows.forEach((row) => {
    const leafName = row.buStrategicInitiativeLeaf

    if (leafName === rootName) {
      return
    }

    rowsByLeaf.set(leafName, [...(rowsByLeaf.get(leafName) ?? []), row])
  })

  return Array.from(rowsByLeaf.entries())
    .map(([name, groupRows]) => ({
      name,
      rows: groupRows,
      buStrategicInitiativeTips: Array.from(
        new Set(groupRows.flatMap(({ buStrategicInitiativeTips }) => buStrategicInitiativeTips))
      ).sort((left, right) => left.localeCompare(right)),
    }))
    .sort((left, right) => {
      const getLeafOrder = (group: ICockpitProductGroup) =>
        Math.min(
          ...group.rows.map(({ buStrategicInitiativeLeafOrder }) => buStrategicInitiativeLeafOrder)
        )
      const orderDifference = getLeafOrder(left) - getLeafOrder(right)

      return (
        orderDifference || (getAmounts(right.rows).goal ?? 0) - (getAmounts(left.rows).goal ?? 0)
      )
    })
}

const createProductRow = (
  group: ICockpitProductGroup,
  rowId: string,
  parentRowId: string | null,
  geoKey: string,
  geoLabel: string
): ICockpitInitiativeHierarchyRow => ({
  rowId,
  parentRowId,
  level: 'product',
  groupKey: group.name,
  groupLabel: group.name,
  geoKey,
  geoLabel,
  geoDepth: -1,
  partnerId: '',
  initiativeEntryIds: getInitiativeEntryIds(group.rows),
  buStrategicInitiativeTips: group.buStrategicInitiativeTips,
  ...getAmounts(group.rows),
})

const getCategoryProductRows = (
  rows: ICockpitInitiativeRowWithGeo[],
  {
    parentRowId = null,
    rowIdPrefix = parentRowId ? `${parentRowId}::` : '',
    geoKey = '',
    geoLabel = '',
  }: IProductRowsOptions = {}
): ICockpitInitiativeHierarchyRow[] =>
  getOfferingGroups(rows).flatMap((offeringGroup) => {
    const rowId = `${rowIdPrefix}product::${encodeURIComponent(offeringGroup.name)}`
    const offeringRow = createProductRow(offeringGroup, rowId, parentRowId, geoKey, geoLabel)
    const categoryRows = getCockpitInitiativeGroups(offeringGroup.rows, 'category', 'goal').map(
      (categoryGroup) => {
        const categoryRowId = `${rowId}::product::${encodeURIComponent(categoryGroup.name)}`

        return createProductRow(categoryGroup, categoryRowId, rowId, geoKey, geoLabel)
      }
    )

    return [offeringRow, ...categoryRows]
  })

const getProductRows = (
  rows: ICockpitInitiativeRowWithGeo[],
  groupField: TCockpitInitiativeGroupField,
  options: IProductRowsOptions = {}
): ICockpitInitiativeHierarchyRow[] => {
  if (groupField === 'category') {
    return getCategoryProductRows(rows, options)
  }

  const {
    parentRowId = null,
    rowIdPrefix = parentRowId ? `${parentRowId}::` : '',
    geoKey = '',
    geoLabel = '',
  } = options

  return getCockpitInitiativeGroups(rows, groupField, 'goal').flatMap((group) => {
    const rowId = `${rowIdPrefix}product::${encodeURIComponent(group.name)}`
    const productRow = createProductRow(group, rowId, parentRowId, geoKey, geoLabel)
    const childRows = getBuStrategicInitiativeChildGroups(group.rows, group.name, groupField).map(
      (childGroup) => {
        const childRowId = `${rowId}::product::${encodeURIComponent(childGroup.name)}`

        return createProductRow(childGroup, childRowId, rowId, geoKey, geoLabel)
      }
    )

    return [productRow, ...childRows]
  })
}

const getGeoHierarchyRows = (
  rows: ICockpitInitiativeRowWithGeo[],
  groupField: TCockpitInitiativeGroupField,
  geoTree: IGeoMappingTree,
  geoDepth = 0,
  parentRowId: string | null = null,
  rowIdPrefix = parentRowId ? `${parentRowId}::` : ''
): ICockpitInitiativeHierarchyRow[] => {
  return getGeoBuckets(rows, geoDepth, geoTree).flatMap((bucket) => {
    const rowId = `${rowIdPrefix}geo::${encodeURIComponent(bucket.key)}`
    const row: ICockpitInitiativeHierarchyRow = {
      rowId,
      parentRowId,
      level: 'geo',
      groupKey: '',
      groupLabel: '',
      geoKey: bucket.key,
      geoLabel: bucket.label,
      geoDepth,
      partnerId: '',
      initiativeEntryIds: getInitiativeEntryIds(bucket.rows),
      buStrategicInitiativeTips: [],
      ...getAmounts(bucket.rows),
    }
    const productRows = getProductRows(bucket.rows, groupField, {
      parentRowId: rowId,
      geoKey: bucket.key,
      geoLabel: bucket.label,
    })
    const geoRows = getGeoHierarchyRows(bucket.rows, groupField, geoTree, geoDepth + 1, rowId)
    const partnerRows = geoRows.length
      ? []
      : getPartnerBuckets(bucket.rows).flatMap(([partnerId, partnerInitiatives]) => {
          const partnerRowId = `${rowId}::partner::${encodeURIComponent(partnerId)}`
          const partnerRow: ICockpitInitiativeHierarchyRow = {
            rowId: partnerRowId,
            parentRowId: rowId,
            level: 'partner',
            groupKey: '',
            groupLabel: '',
            geoKey: bucket.key,
            geoLabel: bucket.label,
            geoDepth: geoDepth + 1,
            partnerId,
            initiativeEntryIds: getInitiativeEntryIds(partnerInitiatives),
            buStrategicInitiativeTips: [],
            ...getAmounts(partnerInitiatives),
          }
          const partnerProductRows = getProductRows(partnerInitiatives, groupField, {
            parentRowId: partnerRowId,
            geoKey: bucket.key,
            geoLabel: bucket.label,
          })
          return [partnerRow, ...partnerProductRows]
        })

    return [row, ...productRows, ...geoRows, ...partnerRows]
  })
}

const getProductGeoRows = (
  rows: ICockpitInitiativeRowWithGeo[],
  geoTree: IGeoMappingTree,
  geoDepth = 0,
  parentRowId: string
): ICockpitInitiativeHierarchyRow[] => {
  return getGeoBuckets(rows, geoDepth, geoTree).flatMap((bucket) => {
    const rowId = `${parentRowId}::geo::${encodeURIComponent(bucket.key)}`
    const row: ICockpitInitiativeHierarchyRow = {
      rowId,
      parentRowId,
      level: 'geo',
      groupKey: '',
      groupLabel: '',
      geoKey: bucket.key,
      geoLabel: bucket.label,
      geoDepth,
      partnerId: '',
      initiativeEntryIds: getInitiativeEntryIds(bucket.rows),
      buStrategicInitiativeTips: [],
      ...getAmounts(bucket.rows),
    }
    const children = getProductGeoRows(bucket.rows, geoTree, geoDepth + 1, rowId)

    return [row, ...children]
  })
}

const getProductGeoHierarchyRows = (
  rows: ICockpitInitiativeRowWithGeo[],
  groupField: TCockpitInitiativeGroupField,
  geoTree: IGeoMappingTree,
  parentRowId: string | null = null
): ICockpitInitiativeHierarchyRow[] => {
  if (groupField === 'category') {
    return getOfferingGroups(rows).flatMap((offeringGroup) => {
      const rowId = `product::${encodeURIComponent(offeringGroup.name)}`
      const offeringRow = createProductRow(offeringGroup, rowId, parentRowId, '', '')
      const categoryRows = getCockpitInitiativeGroups(
        offeringGroup.rows,
        'category',
        'goal'
      ).flatMap((categoryGroup) => {
        const categoryRowId = `${rowId}::product::${encodeURIComponent(categoryGroup.name)}`
        const categoryRow = createProductRow(categoryGroup, categoryRowId, rowId, '', '')

        return [categoryRow, ...getProductGeoRows(categoryGroup.rows, geoTree, 0, categoryRowId)]
      })

      return [
        offeringRow,
        ...getProductGeoRows(offeringGroup.rows, geoTree, 0, rowId),
        ...categoryRows,
      ]
    })
  }

  return getCockpitInitiativeGroups(rows, groupField, 'goal').flatMap((group) => {
    const rowId = `product::${encodeURIComponent(group.name)}`
    const productRow = createProductRow(group, rowId, parentRowId, '', '')
    const childRows = getBuStrategicInitiativeChildGroups(
      group.rows,
      group.name,
      groupField
    ).flatMap((childGroup) => {
      const childRowId = `${rowId}::product::${encodeURIComponent(childGroup.name)}`
      const childRow = createProductRow(childGroup, childRowId, rowId, '', '')

      return [childRow, ...getProductGeoRows(childGroup.rows, geoTree, 0, childRowId)]
    })

    return [productRow, ...getProductGeoRows(group.rows, geoTree, 0, rowId), ...childRows]
  })
}

/**
 * Builds the full hierarchy in the selected dimension order: either
 * Total > GeoMapping > Partner > Product or Total > Product > GeoMapping. Partners are leaves of
 * the primary Geo hierarchy only; when Geo is the second dimension, its countries stay terminal.
 * BU Strategic Initiative Product groups additionally expose their original Development leaves
 * beneath the JBP Settings root. Total is a fake common root of the selected first dimension, not
 * a separate summary row.
 * Existing KI grouping rules stay authoritative: Period expands into quarter shares only when
 * selected, while multi-value business priorities may place an initiative under several Product
 * rows. In Geo-first order, Product rows are emitted for every Geo level, not only the leaf.
 */
export const getCockpitInitiativeHierarchyRows = (
  rows: ICockpitInitiativeRowWithGeo[],
  groupField: TCockpitInitiativeGroupField,
  showGeoHierarchy = true,
  hierarchyOrder: TCockpitInitiativesHierarchyOrder = 'geoProduct',
  geoMappingItems: IMappedGeoMappingItem[] = []
): ICockpitInitiativeHierarchyRow[] => {
  const geoTree = buildGeoMappingTree(geoMappingItems)
  const primaryHierarchyRows = showGeoHierarchy
    ? hierarchyOrder === 'geoProduct'
      ? getGeoHierarchyRows(rows, groupField, geoTree, 0, COCKPIT_INITIATIVE_TOTAL_ROW_ID, '')
      : getProductGeoHierarchyRows(rows, groupField, geoTree, COCKPIT_INITIATIVE_TOTAL_ROW_ID)
    : getProductRows(rows, groupField, {
        parentRowId: COCKPIT_INITIATIVE_TOTAL_ROW_ID,
        rowIdPrefix: '',
      })
  const secondaryHierarchyRows = !showGeoHierarchy
    ? []
    : hierarchyOrder === 'geoProduct'
      ? getProductRows(rows, groupField, {
          parentRowId: COCKPIT_INITIATIVE_TOTAL_ROW_ID,
        })
      : getProductGeoRows(rows, geoTree, 0, COCKPIT_INITIATIVE_TOTAL_ROW_ID)
  const totalRow: ICockpitInitiativeHierarchyRow = {
    rowId: COCKPIT_INITIATIVE_TOTAL_ROW_ID,
    parentRowId: null,
    level: 'total',
    groupKey: '',
    groupLabel: '',
    geoKey: '',
    geoLabel: '',
    geoDepth: -1,
    partnerId: '',
    initiativeEntryIds: getInitiativeEntryIds(rows),
    buStrategicInitiativeTips: [],
    ...getAmounts(rows),
  }

  return [totalRow, ...secondaryHierarchyRows, ...primaryHierarchyRows]
}
